import {
    IElementCalculations,
    IScrollPositionResult,
    IScrollToPositionParams,
    IVisibilityDetectionParams,
} from "./types";
import { ITEMS_FOR_SINGLE_LINE } from "../../constants";
import { UserCategory } from "../../types";

const avgCharWidth = 7;

export const truncateName = (name: string, count: number, width: number): string => {
    const countStr = ` (${count})`;
    // Compute the approximate maximum characters per line.
    const maxCharsPerLine = Math.floor((width + 20) / avgCharWidth);
    // Reserve space for the count on the second line.
    const availableSecond = maxCharsPerLine - countStr.length;

    // If the full text fits on one line, return it.
    if (name.length + countStr.length <= maxCharsPerLine) {
        return name + countStr;
    }

    const words = name.split(" ");
    // Special case: if there is only one word, truncate that word if needed.
    if (words.length === 1) {
        if (words[0].length <= maxCharsPerLine) {
            return words[0] + countStr;
        }
        return maxCharsPerLine > 3
            ? words[0].slice(0, maxCharsPerLine - 3) + "..." + countStr
            : words[0].slice(0, maxCharsPerLine) + countStr;
    }

    // --- Determine the first line ---
    // We want as many whole words as possible in the first line (without any ellipses)
    // so that it doesn’t exceed maxCharsPerLine.
    let splitIndex = 1; // at least one word goes on the first line
    for (let i = 1; i < words.length; i++) {
        const candidate = words.slice(0, i + 1).join(" ");
        if (candidate.length <= maxCharsPerLine) {
            splitIndex = i + 1;
        } else {
            break;
        }
    }
    const firstLine = words.slice(0, splitIndex).join(" ");
    // --- Determine the second line ---
    let secondLine = words.slice(splitIndex).join(" ");
    // If the second line is too long (i.e. its length exceeds the allowed characters),
    // truncate it and add an ellipsis.
    if (secondLine.length > availableSecond) {
        secondLine =
            availableSecond > 3
                ? secondLine.slice(0, availableSecond - 3) + "..."
                : secondLine.slice(0, availableSecond);
    }

    return firstLine + "\n" + secondLine + countStr;
};

// Shared calculation function to avoid duplicate DOM queries
const getElementCalculations = ({
    selectedRef,
    containerRef,
    categoryList,
    categoryId,
    PADDING_LEFT,
}: IVisibilityDetectionParams | IScrollToPositionParams): IElementCalculations | null => {
    if (!selectedRef.current || !containerRef.current) return null;

    const container = containerRef.current;
    const selectedElement = selectedRef.current;
    const index = categoryList?.findIndex((item) => item?.categoryId === categoryId);
    if (index === -1) return null;

    // Calculate element and container dimensions
    const selectedItemWidth = selectedElement.offsetWidth;
    const containerWidth = container.clientWidth;
    const positionFromLeft = index * (selectedItemWidth + PADDING_LEFT) + PADDING_LEFT;
    // Calculate viewport and element boundaries
    const currentScrollLeft = container.scrollLeft;
    const viewportLeft = currentScrollLeft;
    const viewportRight = currentScrollLeft + containerWidth;
    const elementLeft = positionFromLeft;
    const elementRight = positionFromLeft + selectedItemWidth;

    return {
        index,
        selectedItemWidth,
        containerWidth,
        positionFromLeft,
        elementLeft,
        elementRight,
        viewportLeft,
        viewportRight,
    };
};

export const isElementFullyVisible = (params: IVisibilityDetectionParams): boolean => {
    if (!params.categoryId) return false;
    const calculations = getElementCalculations(params);
    if (!calculations) return false;
    const { elementLeft, elementRight, viewportLeft, viewportRight } = calculations;
    return elementLeft >= viewportLeft && elementRight <= viewportRight;
};

export const getScrollPosition = (
    params: IScrollToPositionParams
): IScrollPositionResult | undefined => {
    if (!params.categoryId) {
        return { scrollPosition: 0, isFullyVisible: false };
    }
    const calculations = getElementCalculations(params);
    if (!calculations) return undefined;

    const {
        positionFromLeft,
        selectedItemWidth,
        containerWidth,
        elementLeft,
        elementRight,
        viewportLeft,
        viewportRight,
    } = calculations;
    // Calculate scroll position to center the element
    const scrollPosition = positionFromLeft - containerWidth / 2 + selectedItemWidth / 2;
    const isFullyVisible = elementLeft >= viewportLeft && elementRight <= viewportRight;

    return { scrollPosition, isFullyVisible };
};

export const getHPosVPosOfCategory = (index: number, total: number) => {
    if (total <= 0 || index < 0 || index >= total) return { hpos: 1, vpos: 1 };
    if (total <= ITEMS_FOR_SINGLE_LINE) return { hpos: 1, vpos: index + 1 };
    const firstHalf = Math.ceil(total / 2);
    return index < firstHalf
        ? { hpos: 1, vpos: index + 1 }
        : { hpos: 2, vpos: index - firstHalf + 1 };
};

export const createCategoryAnalyticsContext = ({
    name,
    categoryId,
    tableNo,
    tableId,
    pin,
    restaurantId,
    isUserHost,
    parentType,
}: {
    name: string;
    categoryId: string | number;
    tableNo: string | number;
    tableId: string;
    pin: string | number;
    restaurantId: string;
    isUserHost: boolean;
    parentType: string;
}) => {
    return JSON.stringify({
        widget_type: `Native_QR_Menu_StackedWidget-->${parentType}`,
        collection_name: name,
        collection_id: categoryId,
        property_type: `Native_QR_Menu_StackedWidget-->${name}`,
        table_no: tableNo,
        table_id: tableId,
        pin: pin,
        restaurant_id: restaurantId,
        "user-category": isUserHost ? UserCategory.PRIMARY : UserCategory.SECONDARY,
    });
};
